import StructureControl from "../../../../../core/gd/structure-control/structure-control"
import Project from "../../../../../core/model2/project/project"
import ProjectSession from "../../../../../core/model2/project/project-session"
import {DraggedOverPosition} from "../../../../../core/gd-modules/dnd/@types"
import {ProjectsTreeSortType} from "../../../../../core/model2/space/@types"
import {SpaceProto} from "../../../../../core/model2/space/space-proto"
import NavigationMenu_ProjectsLocalSettings from "../../../local-settings"
import {
    MENU_PROJECTS_DND_DRAGGED_OUT,
    MENU_PROJECTS_DND_HOVER,
    MenuProjectDropResult
} from "./@events"
import menuProjects_dropProject from "./drop-project"
import {MenuProjectsDropRect} from "./drop-rect"
import {MENU_PROJECTS_TREE_ROOT_ID} from "./@const"
import menuProjects_lastVisibleDescendant from "./last-visible-descendant"

export type MenuProjectDndProps = {
    project         : ProjectSession
    space           : SpaceProto
    localSettings   : NavigationMenu_ProjectsLocalSettings
    isExpanded      : boolean

    parentId        : ID                    // parent of the sibling list this row is rendered in
    siblings        : ProjectSession[]      // that list, in render order
}

export type MenuProjectDndComponent = {
    props: MenuProjectDndProps
}

// const debug = "MenuProjectDnd"
const debug = false

// Ported from core/gd-modules/tasks-view/core/manager/dnd/project-dnd.ts - same three zone hit test,
// same restrictions, same TOP/CENTER/BOTTOM semantics. Adapted to ProjectSession (the menu has no
// ItemModelV2, so expansion comes from localSettings and ordering from the space sort map).
export default class MenuProjectDnd {

    public dndEnabled       : boolean = true    // false while the row is being renamed inline
    public props            : MenuProjectDndProps
    private draggedOver?    : DraggedOverPosition

    constructor(props: MenuProjectDndProps) {
        this.props = props
    }

    getDNDId = () => {
        return this.props.project.id
    }

    // positions only mean something under manual sorting - A-Z leaves nothing to reorder
    private get isManualSorting(): boolean {
        if (gd.user.acl.isClient) {
            return true     // customer portal is always manual - see projects-reorder-list
        }

        return this.props.space.projectsSorting !== ProjectsTreeSortType.ALPHABETICAL
    }

    // the whole restriction set (no self drop, no drop into own descendant, target needs
    // canCreateProject, workspaces never nest into a project) already lives in StructureControl.
    // Dropping into a row always reparents the dragged project, so it also needs full access to it -
    // a plain reorder does not, see _isCrossParentDrop
    private _checkCanDropInto = (draggedProject: ProjectSession): boolean => {
        const res = StructureControl.canMoveProjectIntoProject(
                draggedProject as unknown as Project,
                this.props.project as unknown as Project
            )
            && StructureControl.canMoveProject(draggedProject.id)

        debug && console.log(debug, '_checkCanDropInto', res, this.props.project.name)

        return res
    }

    // a between-rows drop only reparents the dragged project when it lands in a different list than
    // its own siblings - dropped back among them it is a plain reorder, which needs no access check
    private _isCrossParentDrop = (draggedProject: ProjectSession): boolean => {
        const currentParentId = draggedProject.parentId ?? MENU_PROJECTS_TREE_ROOT_ID

        return currentParentId !== this.props.parentId
    }

    // a collapsed row hides its children so its bottom edge reads as "after me", an expanded one
    // reads as "first child of me". The centre band always means "into".
    private _isDropInto = (draggedOver?: DraggedOverPosition): boolean => {
        return draggedOver === DraggedOverPosition.CENTER
            || (this.props.isExpanded && draggedOver === DraggedOverPosition.BOTTOM)
    }

    canDrag(superResult: boolean): boolean {
        return this.dndEnabled && superResult
    }

    canDrop(superResult: boolean, draggedComponent?: MenuProjectDndComponent): boolean {
        if (!draggedComponent) {
            return superResult
        }

        if (draggedComponent.props.project.id === this.props.project.id) {
            return superResult
        }

        return true
    }

    onDraggedOut(draggedComponent: MenuProjectDndComponent, isOnDrop: boolean) {
        if (!isOnDrop) {
            this.draggedOver = undefined
        }

        gd.bus.triggerEvent(MENU_PROJECTS_DND_DRAGGED_OUT, {
            project: this.props.project
        })
    }

    // forget the zone, so the next onDraggingOver in it fires MENU_PROJECTS_DND_HOVER again instead of
    // being deduped away with no line. Called when another row takes the line (both are dragged over on
    // the pixel line two drop rects share, see drop-rect.ts) and after onDrop, which onDraggedOut(isOnDrop)
    // kept the zone for
    resetDraggedOver() {
        this.draggedOver = undefined
    }

    // dropRect comes from MenuProject_DndItem, not from the row element - it covers the gap below the
    // row as well, so a cursor between two rows still resolves to one of them
    onDraggingOver(draggedComponent: MenuProjectDndComponent, mouseEvent: any, dropRect: MenuProjectsDropRect) {
        const draggedProject = draggedComponent.props.project

        if (draggedProject.id === this.props.project.id) {
            return
        }

        let canDrop = true

        // an expanded row's bottom edge means "drop in as first child", so it needs the into check
        if (this.props.isExpanded && this.draggedOver === DraggedOverPosition.BOTTOM) {
            canDrop = this._checkCanDropInto(draggedProject)
        }

        // workspaces can only be reordered among root projects
        if (draggedProject.isWorkspace) {
            canDrop = this.props.project.isRoot || this.props.project.isWorkspace
        }

        if (!canDrop) {
            return
        }

        const over = gd.utils.Dom.checkMouseOverRectArea(mouseEvent, dropRect, 2, 3)

        let newDraggedOver
        switch (over && over.yLine) {
            case 0:
                newDraggedOver = DraggedOverPosition.TOP
                break

            case 1:
                newDraggedOver = DraggedOverPosition.CENTER
                canDrop = this._checkCanDropInto(draggedProject)
                break

            case 2:
                newDraggedOver = DraggedOverPosition.BOTTOM
                break
        }

        // a between-rows drop that reparents needs the same access a drop-into needs - reordering
        // among the current siblings does not
        if (
            canDrop
            && newDraggedOver
            && !this._isDropInto(newDraggedOver)
            && this._isCrossParentDrop(draggedProject)
        ) {
            canDrop = StructureControl.canMoveProject(draggedProject.id)
        }

        // alphabetical sorting leaves nothing to reorder - only the into-project bands stay droppable
        if (
            !this.isManualSorting
            && !this._isDropInto(newDraggedOver)
        ) {
            canDrop = false
        }

        debug && console.log(debug, 'onDraggingOver', canDrop, newDraggedOver)

        if (canDrop && !!newDraggedOver && this.draggedOver !== newDraggedOver) {
            this.draggedOver = newDraggedOver

            const isDropInto = this._isDropInto(this.draggedOver)

            // dropping into this row makes the row itself the parent; a between-rows reorder leaves both
            // flanking rows' shared parent unchanged - the preview must show whichever one actually becomes
            // the parent, not the row the cursor happens to be over
            const target = isDropInto ? this.props.project : this.props.project.treeParent

            // the line has to land on the same pixels whichever side of the boundary the cursor is on,
            // but it is drawn on a row via a CSS offset (dnd.scss) that two different rows' boxes can
            // round to different device pixels - so only the row ABOVE the boundary ever draws it. A TOP
            // hit is redirected to the row above's own BOTTOM instead of drawing on this row's TOP. The
            // row above is the previous sibling's own last expanded descendant when it has one (its
            // children render between it and this row, see menuProjects_lastVisibleDescendant), and the
            // sibling itself otherwise. The very first row of a list has no previous sibling to redirect
            // to and keeps hover-top.
            let lineProject     = this.props.project
            let lineDraggedOver = this.draggedOver

            if (this.draggedOver === DraggedOverPosition.TOP) {
                const index = _.findIndex(this.props.siblings, (s: ProjectSession) => {
                    return s.id === this.props.project.id
                })

                const prevSibling = index > 0 ? this.props.siblings[index - 1] : undefined

                if (prevSibling) {
                    lineProject     = menuProjects_lastVisibleDescendant(prevSibling, this.props.localSettings, this.props.space)
                    lineDraggedOver = DraggedOverPosition.BOTTOM
                }
            }

            gd.bus.triggerEvent(MENU_PROJECTS_DND_HOVER, {
                project     : lineProject,
                draggedOver : lineDraggedOver,
                isDropInto  : isDropInto,
                target      : target,
                showTarget  : !!target && target.id !== draggedProject.parentId,
            })
        }
    }

    onDrop(draggedComponent: MenuProjectDndComponent, dropResult: any): any {
        const draggedProject = draggedComponent.props.project

        // no recorded position means this row never fired a hover event, so its target was never
        // shown in the preview - dropping here must not silently move the project in regardless
        if (!this.draggedOver) {
            debug && console.log(debug, 'onDrop rejected - untracked position', this.props.project.name)

            return {}
        }

        const isIntoDrop = this._isDropInto(this.draggedOver)

        if (isIntoDrop && !this._checkCanDropInto(draggedProject)) {
            debug && console.log(debug, 'onDrop rejected', this.props.project.name)

            return {}
        }

        if (
            !isIntoDrop
            && this._isCrossParentDrop(draggedProject)
            && !StructureControl.canMoveProject(draggedProject.id)
        ) {
            debug && console.log(debug, 'onDrop rejected - cross-parent reorder needs access', this.props.project.name)

            return {}
        }

        // DndManager chains onDrop through every dragged-over target, and on the pixel line two drop
        // rects share there are two. The verdict must not inherit the previous target's - an expanded
        // upper row leaves isDropInto behind, and menuProjects_dropProject checks that flag first
        return _.assign({}, dropResult, {
            targetProject   : this.props.project,
            type            : this.draggedOver,
            targetParentId  : this.props.parentId,
            targetSiblings  : this.props.siblings,
            isDropInto      : isIntoDrop,
            isReorder       : !isIntoDrop,
        })
    }

    onDragEnd(dropResult: MenuProjectDropResult) {
        debug && console.log(debug, 'onDragEnd', dropResult)

        this.draggedOver = undefined

        gd.bus.triggerEvent(MENU_PROJECTS_DND_HOVER, {
            project: this.props.project
        })

        if (_.isEmpty(dropResult) || !dropResult.targetProject) {
            return
        }

        menuProjects_dropProject({
            draggedProject  : this.props.project,
            space           : this.props.space,
            localSettings   : this.props.localSettings,
            dropResult      : dropResult,
        })
    }
}
